import { apiPost } from "../client";
import { graphqlRequest } from "../graphqlClient";
import type {
  OnboardingState,
  OnboardingStateViewConnection,
  OnboardingStepViewConnection,
} from "../models/onboarding";

interface OnboardingStateSeriesResponse {
  onboarding: {
    onboardingStateViewList: OnboardingStateViewConnection;
  };
}

interface OnboardingStateStepsResponse {
  onboarding: {
    onboardingStepViewList: OnboardingStepViewConnection;
  };
}

/**
 * The series and step reads are deliberately separate documents: sibling root
 * fields resolve in parallel against the same request-scoped DbContext, which
 * fails with "A second operation was started on this context instance".
 */
const ONBOARDING_STATE_SERIES_QUERY = `
  query OnboardingStateSeries {
    onboarding {
      onboardingStateViewList(first: 1) {
        edges {
          node {
            id
            onboardingSeriesId
            onboardingStatus
            onboardingStartedDate
            onboardingCompletedDate
            onboardingRestartCount
          }
        }
      }
    }
  }
`;

const ONBOARDING_STATE_STEPS_QUERY = `
  query OnboardingStateSteps {
    onboarding {
      onboardingStepViewList(first: 100) {
        edges {
          node {
            id
            onboardingSeriesId
            isCurrentSeries
            stepNumber
            stepName
            stepStatus
            startedByUserId
            startedAt
            completedByUserId
            completedDate
          }
        }
      }
    }
  }
`;

const EMPTY_STATE: OnboardingState = {
  seriesId: null,
  status: "NotStarted",
  startedDate: null,
  completedDate: null,
  restartCount: 0,
  steps: [],
};

/**
 * Reads the tenant onboarding state. Onboarding reads always go through GraphQL —
 * there is no REST GET for onboarding.
 */
export async function getOnboardingState(): Promise<OnboardingState> {
  const seriesData = await graphqlRequest<OnboardingStateSeriesResponse>(
    ONBOARDING_STATE_SERIES_QUERY,
  );

  const series = seriesData.onboarding.onboardingStateViewList.edges[0]?.node;
  if (!series) return EMPTY_STATE;

  const stepsData = await graphqlRequest<OnboardingStateStepsResponse>(
    ONBOARDING_STATE_STEPS_QUERY,
  );

  const steps = stepsData.onboarding.onboardingStepViewList.edges
    .map((edge) => edge.node)
    .filter((node) => node.isCurrentSeries)
    .sort((a, b) => a.stepNumber - b.stepNumber)
    .map((node) => ({
      stepNumber: node.stepNumber,
      stepName: node.stepName,
      stepStatus: node.stepStatus,
      startedByUserId: node.startedByUserId,
      startedAt: node.startedAt,
      completedByUserId: node.completedByUserId,
      completedDate: node.completedDate,
    }));

  return {
    seriesId: series.onboardingSeriesId,
    status: series.onboardingStatus,
    startedDate: series.onboardingStartedDate,
    completedDate: series.onboardingCompletedDate,
    restartCount: series.onboardingRestartCount,
    steps,
  };
}

export async function startOnboarding(): Promise<void> {
  return apiPost<void>("onboarding/start");
}

export async function arriveAtOnboardingStep(
  stepNumber: number,
): Promise<void> {
  return apiPost<void>(`onboarding/steps/${stepNumber}/arrive`);
}

export async function completeOnboardingStep(
  stepNumber: number,
): Promise<void> {
  return apiPost<void>(`onboarding/steps/${stepNumber}/complete`);
}

export async function skipOnboardingStep(stepNumber: number): Promise<void> {
  return apiPost<void>(`onboarding/steps/${stepNumber}/skip`);
}

/** Force-completes onboarding, recording every outstanding step as skipped. */
export async function completeOnboarding(): Promise<void> {
  return apiPost<void>("onboarding/complete");
}

export async function restartOnboarding(): Promise<void> {
  return apiPost<void>("onboarding/restart");
}
